import React from 'react';
import { Box, Container, Typography, Grid, Divider, Stack } from '@mui/material';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import PaymentIcon from '@mui/icons-material/Payment';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

export default function RefundPolicy() {
  const refundIcon = (
    <AssignmentReturnIcon 
      sx={{ 
        fontSize: 40,
        color: "#f0f0f0" 
      }} 
    />
  );

  const paymentIcon = (
    <PaymentIcon 
      sx={{ 
        fontSize: 40,
        color: "#f0f0f0" 
      }} 
    />
  );

  const timeframeIcon = (
    <ScheduleIcon 
      sx={{ 
        fontSize: 40,
        color: "#f0f0f0" 
      }} 
    />
  );

  const supportIcon = (
    <SupportAgentIcon 
      sx={{ 
        fontSize: 40,
        color: "#f0f0f0" 
      }} 
    />
  );

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: "#080808",
        py: 10,
        position: "relative",
        color: "#e4e2e2"
      }}
    >
      <Container maxWidth="lg">
        {/* Header Section */}
        <Grid container spacing={4} sx={{ mb: 6 }}>
          <Grid item xs={12} md={4} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            {refundIcon}
          </Grid>
          <Grid item xs={12} md={8}>
            <Typography
              variant="h3"
              sx={{
                color: "#fefdfd",
                fontFamily: "'Spline Sans Mono', monospace",
                fontWeight: 600,
                fontStyle: "italic",
                fontSize: { xs: "24px", md: "32px" },
                lineHeight: "1.2",
                mb: 3
              }}
            >
              Refund Policy
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#7a7976",
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: "15px",
                lineHeight: "29.1px",
              }}
            >
              Last Updated: April 9, 2025
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ bgcolor: "#333", mb: 6 }} />

        {/* Introduction */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h5"
            sx={{
              color: "#fefdfd",
              fontFamily: "'Spline Sans Mono', monospace",
              fontWeight: 600,
              fontSize: "22px",
              mb: 3
            }}
          >
            1. Introduction
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#7a7976",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "15px",
              lineHeight: "1.8",
            }}
          >
            Thank you for choosing our Secure Data Transfer Service. We value your satisfaction and have established this refund policy to ensure transparency regarding refunds and cancellations. This policy outlines when and how you can request a refund for our services and the conditions under which refunds will be processed.
          </Typography>
        </Box>

        {/* Feature cards */}
        <Grid container spacing={4} sx={{ mb: 6, flexWrap: {xs: "wrap", sm: "nowrap"} }}>
          <Grid item xs={12} md={4}>
            <Stack alignItems="center" spacing={2} sx={{ p: 3, bgcolor: "#111", borderRadius: 2, height: "100%" }}>
              <Box sx={{ height: 80, display: "flex", alignItems: "center" }}>
                {paymentIcon}
              </Box>
              <Typography
                variant="h6"
                align="center"
                sx={{
                  color: "#e4e2e2",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 500,
                  fontSize: "21px",
                  lineHeight: "25.2px",
                }}
              >
                Refund Eligibility
              </Typography>
              <Typography
                variant="body2"
                align="center"
                sx={{
                  color: "#71706b",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: "14px",
                  lineHeight: "1.6",
                }}
              >
                Understand when your purchase qualifies for a refund based on subscription type and circumstances.
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack alignItems="center" spacing={2} sx={{ p: 3, bgcolor: "#111", borderRadius: 2, height: "100%" }}>
              <Box sx={{ height: 80, display: "flex", alignItems: "center" }}>
                {timeframeIcon}
              </Box>
              <Typography
                variant="h6"
                align="center"
                sx={{
                  color: "#e4e2e2",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 500,
                  fontSize: "21px",
                  lineHeight: "25.2px",
                }}
              >
                Refund Timeframes
              </Typography>
              <Typography
                variant="body2"
                align="center"
                sx={{
                  color: "#71706b",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: "14px",
                  lineHeight: "1.6",
                }}
              >
                Clear timelines for when you can request refunds and how long processing will take.
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack alignItems="center" spacing={2} sx={{ p: 3, bgcolor: "#111", borderRadius: 2, height: "100%" }}>
              <Box sx={{ height: 80, display: "flex", alignItems: "center" }}>
                {supportIcon}
              </Box>
              <Typography
                variant="h6"
                align="center"
                sx={{
                  color: "#e4e2e2",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 500,
                  fontSize: "21px",
                  lineHeight: "25.2px",
                }}
              >
                24/7 Support
              </Typography>
              <Typography
                variant="body2"
                align="center"
                sx={{
                  color: "#71706b",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: "14px",
                  lineHeight: "1.6",
                }}
              >
                Our dedicated team is available around the clock to assist with any refund questions or concerns.
              </Typography>
            </Stack>
          </Grid>
        </Grid>

        {/* Refund Eligibility */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h5"
            sx={{
              color: "#fefdfd",
              fontFamily: "'Spline Sans Mono', monospace",
              fontWeight: 600,
              fontSize: "22px",
              mb: 3
            }}
          >
            2. Refund Eligibility
          </Typography>
          
          <Typography
            variant="h6"
            sx={{
              color: "#e4e2e2",
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 500,
              fontSize: "18px",
              mb: 2
            }}
          >
            2.1 Service Plan Refunds
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#7a7976",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "15px",
              lineHeight: "1.8",
              mb: 2
            }}
          >
            Refund eligibility varies based on your subscription plan:
          </Typography>
          <Box component="ul" sx={{ color: "#7a7976", pl: 4, mb: 3 }}>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>
              <strong>Starter Plan ($18):</strong> Eligible for a full refund within 7 days of purchase if the service has not been used.
            </Typography>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>
              <strong>Advanced Plan ($48):</strong> Eligible for a full refund within 14 days of purchase if less than 20% of the service features have been utilized.
            </Typography>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>
              <strong>Ultimate Plan ($85):</strong> Eligible for a prorated refund within 30 days of purchase based on usage.
            </Typography>
            <Typography component="li" sx={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              <strong>Supreme Plan ($170):</strong> Eligible for a prorated refund within 30 days of purchase based on usage and a 15% administrative fee.
            </Typography>
          </Box>
          
          <Typography
            variant="h6"
            sx={{
              color: "#e4e2e2",
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 500,
              fontSize: "18px",
              mb: 2
            }}
          >
            2.2 Non-Refundable Items
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#7a7976",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "15px",
              lineHeight: "1.8",
              mb: 2
            }}
          >
            The following are not eligible for refunds:
          </Typography>
          <Box component="ul" sx={{ color: "#7a7976", pl: 4 }}>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Services that have been fully delivered</Typography>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Custom domain registrations and configurations</Typography>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Services where the refund period has expired</Typography>
            <Typography component="li" sx={{ fontFamily: "'Space Grotesk', sans-serif" }}>Add-on services purchased separately from main subscription plans</Typography>
          </Box>
        </Box>

        {/* Refund Process */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h5"
            sx={{
              color: "#fefdfd",
              fontFamily: "'Spline Sans Mono', monospace",
              fontWeight: 600,
              fontSize: "22px",
              mb: 3
            }}
          >
            3. Refund Process
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#7a7976",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "15px",
              lineHeight: "1.8",
              mb: 2
            }}
          >
            To request a refund, please follow these steps:
          </Typography>
          <Box component="ol" sx={{ color: "#7a7976", pl: 4 }}>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>
              Contact our customer support team via email at refunds@yourdatatransferservice.com or through the support portal in your account dashboard.
            </Typography>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>
              Provide your account information, purchase details, and reason for requesting a refund.
            </Typography>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>
              Our team will review your request and respond within 2 business days.
            </Typography>
            <Typography component="li" sx={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              If approved, refunds will be processed to the original payment method within 5-10 business days.
            </Typography>
          </Box>
        </Box>

        {/* Refund Timeframes */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h5"
            sx={{
              color: "#fefdfd",
              fontFamily: "'Spline Sans Mono', monospace",
              fontWeight: 600,
              fontSize: "22px",
              mb: 3
            }}
          >
            4. Refund Timeframes
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#7a7976",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "15px",
              lineHeight: "1.8",
              mb: 2
            }}
          >
            Refund processing times:
          </Typography>
          <Box component="ul" sx={{ color: "#7a7976", pl: 4 }}>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Credit/Debit Card Refunds: 5-7 business days</Typography>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>PayPal Refunds: 3-5 business days</Typography>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Bank Transfers: 7-10 business days</Typography>
            <Typography component="li" sx={{ fontFamily: "'Space Grotesk', sans-serif" }}>Cryptocurrency Refunds: 1-3 business days (subject to network conditions)</Typography>
          </Box>
        </Box>

        {/* Cancellation Policy */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h5"
            sx={{
              color: "#fefdfd",
              fontFamily: "'Spline Sans Mono', monospace",
              fontWeight: 600,
              fontSize: "22px",
              mb: 3
            }}
          >
            5. Cancellation Policy
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#7a7976",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "15px",
              lineHeight: "1.8",
              mb: 2
            }}
          >
            Subscription cancellations:
          </Typography>
          <Box component="ul" sx={{ color: "#7a7976", pl: 4 }}>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Monthly subscriptions can be canceled at any time. Service will continue until the end of the billing period.</Typography>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Annual subscriptions canceled before the end of the term may be eligible for a prorated refund minus applicable fees.</Typography>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>To cancel, log into your account and navigate to the "Subscription" section or contact customer support.</Typography>
            <Typography component="li" sx={{ fontFamily: "'Space Grotesk', sans-serif" }}>Automatic renewals can be disabled in your account settings at any time.</Typography>
          </Box>
        </Box>

        {/* Exceptional Circumstances */}
        <Box sx={{ mb: 6, p: 4, bgcolor: "#111", borderRadius: 2 }}>
          <Typography
            variant="h5"
            sx={{
              color: "#fefdfd",
              fontFamily: "'Spline Sans Mono', monospace",
              fontWeight: 600,
              fontSize: "22px",
              mb: 3
            }}
          >
            6. Exceptional Circumstances
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#7a7976",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "15px",
              lineHeight: "1.8",
              mb: 2
            }}
          >
            We understand that exceptional circumstances may arise. In the following situations, we may consider refunds outside our standard policy:
          </Typography>
          <Box component="ul" sx={{ color: "#7a7976", pl: 4 }}>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Extended service outages (more than 24 hours)</Typography>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Technical issues preventing service usage that cannot be resolved</Typography>
            <Typography component="li" sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Major service changes that significantly impact functionality</Typography>
            <Typography component="li" sx={{ fontFamily: "'Space Grotesk', sans-serif" }}>Documented hardship cases (evaluated on a case-by-case basis)</Typography>
          </Box>
        </Box>

        {/* Contact Information */}
        <Box sx={{ mb: 6, p: 4, bgcolor: "#111", borderRadius: 2 }}>
          <Typography
            variant="h5"
            sx={{
              color: "#fefdfd",
              fontFamily: "'Spline Sans Mono', monospace",
              fontWeight: 600,
              fontSize: "22px",
              mb: 3
            }}
          >
            Contact Us
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#7a7976",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "15px",
              lineHeight: "1.8",
              mb: 2
            }}
          >
            If you have any questions about our Refund Policy, please contact us at:
          </Typography>
          <Box sx={{ color: "#7a7976", pl: 2 }}>
            <Typography sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Email: info@arkyn-tech.com</Typography>
            <Typography sx={{ mb: 1, fontFamily: "'Space Grotesk', sans-serif" }}>Phone: +421 952307969</Typography>
            <Typography sx={{ fontFamily: "'Space Grotesk', sans-serif" }}>Address: Kostolný rad 318/15 Kráľovský Chlmec 077 01</Typography>
          </Box>
        </Box>

        <Divider sx={{ bgcolor: "#333", mb: 4 }} />
        
        <Typography
          variant="body2"
          align="center"
          sx={{
            color: "#71706b",
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: "14px",
            mb: 2
          }}
        >
          © 2025 Arkyn-tech. All rights reserved.
        </Typography>
      </Container>
    </Box>
  );
}